import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import { Sparkles, Calendar, Clock, CheckCircle2, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const publicApi = axios.create({ baseURL: API });

const AgendarPublico = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [step, setStep] = useState(1); // 1: pick slot, 2: form, 3: confirmation
  const [form, setForm] = useState({ attendee_name: "", attendee_email: "", attendee_phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    publicApi.get(`/booking/public/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!date || !page) return;
    setLoadingSlots(true);
    setSlot(null);
    publicApi.get(`/booking/public/${slug}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots || []))
      .finally(() => setLoadingSlots(false));
  }, [date, slug, page]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><span className="spinner" /></div>;
  if (!page) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F6F5] p-4">
      <div className="text-center max-w-md">
        <div className="aura-logo-mark w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white font-display font-bold text-2xl mb-4">A</div>
        <h1 className="font-display text-3xl font-medium">Página no encontrada</h1>
        <p className="text-[#666] mt-3">El link de agendamiento no existe o fue desactivado.</p>
      </div>
    </div>
  );

  const submit = async () => {
    if (!slot || !date || !form.attendee_name || !form.attendee_email) {
      toast.error("Completa nombre, email y selecciona horario");
      return;
    }
    setSubmitting(true);
    try {
      // Build full ISO with date and time
      const slotDt = new Date(`${date}T${slot}:00`);
      const res = await publicApi.post(`/booking/public/${slug}/book`, {
        ...form,
        slot_datetime: slotDt.toISOString(),
      });
      setBooking(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agendar");
    } finally { setSubmitting(false); }
  };

  // Generate next N days
  const today = new Date();
  const dateOptions = [];
  for (let i = 0; i < (page.days_ahead || 30); i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    dateOptions.push(d);
  }

  if (step === 3) {
    const slotDt = new Date(`${date}T${slot}:00`);
    return (
      <div className="min-h-screen bg-[#F6F6F5] flex items-center justify-center p-4">
        <div className="aura-card-flat p-10 max-w-xl w-full text-center" data-testid="confirmation">
          <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={42} />
          </div>
          <div className="label-overline">Confirmado</div>
          <h1 className="font-display text-4xl font-medium mt-2 tracking-tight">¡Tu reunión está agendada!</h1>
          <p className="text-[#666] mt-4 text-base">
            Te enviamos un email a <strong className="text-[#0A0A0A]">{form.attendee_email}</strong> con los detalles.
          </p>
          <div className="aura-card-flat p-5 mt-6 text-left bg-[#F0F0F0]">
            <div className="label-overline">{page.title}</div>
            <div className="font-display text-xl font-medium mt-1">{slotDt.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
            <div className="text-[#666] mt-1">{slot} hrs · {page.duration_minutes} minutos · {page.timezone_label}</div>
            <div className="text-sm mt-2">Con: <span className="font-medium">{page.owner_name}</span></div>
            {page.location_note && <div className="text-xs text-[#666] mt-2 flex items-center gap-1"><MapPin size={11} /> {page.location_note}</div>}
          </div>
          <button onClick={() => { setStep(1); setDate(null); setSlot(null); setForm({ attendee_name: "", attendee_email: "", attendee_phone: "", notes: "" }); }} className="btn-outline mt-6">Agendar otra reunión</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F6F5]" data-testid="booking-public">
      <div className="max-w-5xl mx-auto p-6 md:p-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="aura-logo-mark w-10 h-10 rounded-xl flex items-center justify-center text-white font-display font-bold">A</div>
          <div>
            <div className="font-display font-medium text-base">Aura Rubí OS</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#666]">Agendamiento</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-8">
          {/* Left - meeting info */}
          <div>
            <div className="aura-card-flat p-7 sticky top-6">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: page.color || "#D99D26" }}>
                <Calendar size={20} className="text-[#0A0A0A]" />
              </div>
              <div className="label-overline">Con {page.owner_name}</div>
              <h1 className="font-display text-3xl font-medium mt-2 tracking-tight">{page.title}</h1>
              {page.description && <p className="text-[#666] mt-3 leading-relaxed">{page.description}</p>}
              <div className="mt-5 space-y-2 text-sm">
                <div className="flex items-center gap-2"><Clock size={14} className="text-[#666]" /> {page.duration_minutes} minutos</div>
                <div className="flex items-center gap-2"><Calendar size={14} className="text-[#666]" /> {page.timezone_label}</div>
                {page.location_note && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#666]" /> {page.location_note}</div>}
              </div>
            </div>
          </div>

          {/* Right - slots */}
          <div>
            {step === 1 && (
              <>
                <div className="label-overline mb-3">Paso 1 · Elige fecha y hora</div>
                <h2 className="font-display text-2xl font-medium mb-5">¿Cuándo te viene bien?</h2>

                <div className="aura-card-flat p-5 mb-5">
                  <div className="label-overline mb-3">Elige día</div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {dateOptions.slice(0, 21).map((d) => {
                      const ds = d.toISOString().split("T")[0];
                      const isSel = date === ds;
                      const dayKey = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][d.getDay()];
                      const enabled = page.working_hours?.[dayKey]?.enabled !== false;
                      return (
                        <button
                          key={ds}
                          type="button"
                          onClick={() => enabled && setDate(ds)}
                          disabled={!enabled}
                          data-testid={`date-${ds}`}
                          className={`flex-shrink-0 w-16 py-3 rounded-xl border transition-all ${
                            isSel ? "border-[#D99D26] bg-[#D99D26]/10" : enabled ? "border-[#E5E5E5] hover:border-[#D99D26]/40" : "border-[#F0F0F0] opacity-40 cursor-not-allowed"
                          }`}
                        >
                          <div className="text-[10px] uppercase tracking-wider text-[#666]">{d.toLocaleDateString("es-MX", { weekday: "short" })}</div>
                          <div className="font-display font-medium text-xl mt-1">{d.getDate()}</div>
                          <div className="text-[10px] text-[#666]">{d.toLocaleDateString("es-MX", { month: "short" })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {date && (
                  <div className="aura-card-flat p-5">
                    <div className="label-overline mb-3">Horarios disponibles</div>
                    {loadingSlots ? (
                      <div className="py-6 flex justify-center"><span className="spinner" /></div>
                    ) : slots.length === 0 ? (
                      <div className="text-center py-6 text-[#666] text-sm">Sin horarios disponibles. Prueba otro día.</div>
                    ) : (
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        {slots.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => { setSlot(s); setStep(2); }}
                            data-testid={`slot-${s}`}
                            className={`py-2.5 rounded-lg border text-sm font-medium transition-all ${slot === s ? "border-[#D99D26] bg-[#D99D26] text-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#D99D26]"}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <button onClick={() => setStep(1)} className="btn-ghost text-sm">← Cambiar horario</button>
                </div>
                <div className="aura-card-flat p-5 mb-5 bg-[#D99D26]/10 border-[#D99D26]/30">
                  <div className="text-sm text-[#666]">Reunión seleccionada</div>
                  <div className="font-display text-xl font-medium mt-1">
                    {new Date(`${date}T${slot}:00`).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>

                <div className="label-overline mb-3">Paso 2 · Tus datos</div>
                <h2 className="font-display text-2xl font-medium mb-5">¿Quién agenda?</h2>

                <div className="aura-card-flat p-7 space-y-4">
                  <div><label className="aura-label">Nombre completo *</label><input required className="aura-input" value={form.attendee_name} onChange={(e) => setForm({ ...form, attendee_name: e.target.value })} data-testid="attendee-name" /></div>
                  <div><label className="aura-label">Email *</label><input required type="email" className="aura-input" value={form.attendee_email} onChange={(e) => setForm({ ...form, attendee_email: e.target.value })} data-testid="attendee-email" /></div>
                  <div><label className="aura-label">Teléfono (opcional)</label><input className="aura-input" value={form.attendee_phone} onChange={(e) => setForm({ ...form, attendee_phone: e.target.value })} /></div>
                  <div><label className="aura-label">¿De qué quieres hablar? (opcional)</label><textarea className="aura-input" rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Quiero saber más sobre franquicias, mayoreo, etc." /></div>
                  <button onClick={submit} disabled={submitting} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="confirm-booking">
                    {submitting ? <><span className="spinner" /> Confirmando...</> : <><Sparkles size={14} /> Confirmar reunión</>}
                  </button>
                  <p className="text-[10px] text-center text-[#666]">Al agendar aceptas recibir un email de confirmación. Rubí Perfumería.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgendarPublico;
